import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import DatePicker from '@/components/DatePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUALIFICATIONS = [
  'Level 1',
  'Level 2',
  'Level 3',
  'Level 4',
  'Level 5'
];

export default function CoachesTab() {
  const [coaches, setCoaches] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showMissingInfoDialog, setShowMissingInfoDialog] = useState(false);
  const [coachMissingInfo, setCoachMissingInfo] = useState(null);
  const [coachToDelete, setCoachToDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    bgNumber: '',
    qualification: '',
    phone: ''
  });

  useEffect(() => {
    loadCoaches();
  }, []);

  const loadCoaches = async () => {
    try {
      const response = await axios.get(`${API}/coaches`);
      setCoaches(response.data);
      
      // Check for coaches missing contact info
      const missingInfo = response.data.find(c => !c.phone);
      if (missingInfo) {
        setCoachMissingInfo(missingInfo);
        setShowMissingInfoDialog(true);
      }
    } catch (error) {
      console.error('Failed to load coaches:', error);
      toast.error('Failed to load coaches');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleQualificationChange = (value) => {
    setFormData({ ...formData, qualification: value });
  };

  const handleOpenDialog = (coach = null) => {
    if (coach) {
      setEditing(coach);
      setFormData({
        firstName: coach.firstName,
        lastName: coach.lastName,
        dob: coach.dob,
        bgNumber: coach.bgNumber,
        qualification: coach.qualification,
        phone: coach.phone || ''
      });
    } else {
      setEditing(null);
      setFormData({
        firstName: '',
        lastName: '',
        dob: '',
        bgNumber: '',
        qualification: '',
        phone: ''
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Manual validation
    if (!formData.firstName || !formData.lastName || !formData.dob || !formData.qualification || !formData.phone) {
      toast.error('Please fill in all required fields including contact number');
      return;
    }

    try {
      if (editing) {
        await axios.put(`${API}/coaches/${editing.id}`, formData);
        toast.success('Coach updated successfully');
      } else {
        await axios.post(`${API}/coaches`, formData);
        toast.success('Coach added successfully');
      }
      setShowDialog(false);
      loadCoaches();
    } catch (error) {
      console.error('Failed to save coach:', error);
      toast.error('Failed to save coach');
    }
  };

  const handleDelete = async (coach) => {
    setCoachToDelete(coach);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!coachToDelete) return;

    try {
      await axios.delete(`${API}/coaches/${coachToDelete.id}`);
      toast.success(`${coachToDelete.firstName} ${coachToDelete.lastName}'s profile deleted successfully`);
      loadCoaches();
    } catch (error) {
      console.error('Failed to delete coach:', error);
      toast.error('Failed to delete coach');
    } finally {
      setShowDeleteDialog(false);
      setCoachToDelete(null);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>Coaches</h2>
          <p className="text-gray-600 text-sm mt-1">Manage your club's coaches</p>
        </div>
        <Button
          data-testid="add-coach-button"
          onClick={() => handleOpenDialog()}
          className="bg-[#000000] hover:bg-[#333333]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Coach
        </Button>
      </div>

      {coaches.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">No coaches added yet. Click "Add Coach" to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {coaches.map(coach => (
            <Card key={coach.id} data-testid={`coach-card-${coach.id}`} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-lg">{coach.firstName} {coach.lastName}</CardTitle>
                <CardDescription>{coach.qualification}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">DOB:</span>
                    <span className="font-medium">{coach.dob ? format(new Date(coach.dob), 'dd/MM/yyyy') : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">BG Number:</span>
                    <span className="font-medium">{coach.bgNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Phone:</span>
                    <span className={`font-medium ${!coach.phone ? 'text-red-500' : ''}`}>{coach.phone || 'Missing!'}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`edit-coach-button-${coach.id}`}
                    onClick={() => handleOpenDialog(coach)}
                    size="sm"
                    variant="outline"
                    className="flex-1"
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    data-testid={`delete-coach-button-${coach.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(coach);
                    }}
                    size="sm"
                    variant="outline"
                    className="text-gray-900 hover:bg-gray-100"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Coach' : 'Add Coach'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update coach details' : 'Add a new coach to your club'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                name="firstName"
                data-testid="coach-first-name-input"
                required
                value={formData.firstName}
                onChange={handleChange}
              />
            </div>
            <div>
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                name="lastName"
                data-testid="coach-last-name-input"
                required
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>
            <div>
              <DatePicker
                key={editing ? editing.id : 'new'}
                label="Date of Birth"
                value={formData.dob}
                onChange={(date) => setFormData(prev => ({ ...prev, dob: date }))}
                required
              />
            </div>
            <div>
              <Label htmlFor="bgNumber">BG Number *</Label>
              <Input
                id="bgNumber"
                name="bgNumber"
                data-testid="coach-bg-number-input"
                required
                value={formData.bgNumber}
                onChange={handleChange}
              />
            </div>
            <div>
              <Label htmlFor="qualification">Qualification *</Label>
              <Select value={formData.qualification} onValueChange={handleQualificationChange} required>
                <SelectTrigger data-testid="coach-qualification-select">
                  <SelectValue placeholder="Select qualification" />
                </SelectTrigger>
                <SelectContent>
                  {QUALIFICATIONS.map(qual => (
                    <SelectItem key={qual} value={qual}>{qual}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="phone">Contact Number *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                data-testid="coach-phone-input"
                required
                placeholder="e.g., 07123 456789"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="save-coach-button"
                className="flex-1 bg-[#000000] hover:bg-[#333333]"
              >
                {editing ? 'Update' : 'Add'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Coach</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {coachToDelete?.firstName} {coachToDelete?.lastName}'s profile? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-gray-900 hover:bg-black"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Missing Contact Info Alert Dialog */}
      <AlertDialog open={showMissingInfoDialog} onOpenChange={setShowMissingInfoDialog}>
        <AlertDialogContent className="border-black border-2">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-black">
              Missing Contact Information
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base text-gray-700">
              <strong>{coachMissingInfo?.firstName} {coachMissingInfo?.lastName}</strong> is missing their contact number. 
              Please update their profile to add this required information.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Later</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowMissingInfoDialog(false);
                handleOpenDialog(coachMissingInfo);
              }}
              className="bg-black hover:bg-gray-800"
            >
              Update Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}